import { motion } from 'framer-motion';
import React from 'react';
import styled from 'styled-components';

import { theme } from '../../theme';

interface BorderBoxProps {
  children: React.ReactNode;
  isActive?: boolean;
  onClick?: () => void;
  color?: string;
  className?: string;
  noBackground?: boolean;
}

const StyledBorderBox = styled(motion.div)<{
  $isActive: boolean;
  $color: string;
  $isClickable: boolean;
  $noBackground: boolean;
}>`
  background-color: ${props =>
    props.$noBackground
      ? 'transparent'
      : props.$isActive
        ? `rgba(${props.$color}, 0.1)`
        : 'rgba(0, 20, 0, 0.2)'};
  border: 1px solid ${props => (props.$isActive ? props.$color : `rgba(${props.$color}, 0.2)`)};
  border-radius: 4px;
  padding: 15px;
  transition: all 0.2s ease;
  position: relative;
  cursor: ${props => (props.$isClickable ? 'pointer' : 'default')};

  ${props =>
    props.$isClickable &&
    `
        &:hover {
            border-color: ${props.$color};
            background-color: ${props.$noBackground ? 'transparent' : `rgba(${props.$color}, 0.05)`};
            transform: translateY(-2px);
        }
    `}

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 3px;
    height: 100%;
    background: ${props => (props.$isActive ? props.$color : 'transparent')};
    transition: background 0.2s ease;
  }

  ${props =>
    props.$isClickable &&
    `
        &:hover::before {
            background: ${props.$color};
        }
    `}
`;

// Helper function to convert hex to RGB
const hexToRgb = (hex: string) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
    : '31, 207, 94'; // Default to green if conversion fails
};

const BorderBox: React.FC<BorderBoxProps> = ({
  children,
  isActive = false,
  onClick,
  color = theme.colors.green,
  className,
  noBackground = false,
}) => {
  const colorRgb = hexToRgb(color);
  const isClickable = !!onClick;

  return (
    <StyledBorderBox
      $isActive={isActive}
      $color={colorRgb}
      $isClickable={isClickable}
      $noBackground={noBackground}
      onClick={onClick}
      whileHover={isClickable ? { scale: 1.01 } : undefined}
      whileTap={isClickable ? { scale: 0.99 } : undefined}
      className={className}
    >
      {children}
    </StyledBorderBox>
  );
};

export default BorderBox;
